import React, { Fragment, useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { equals, findIndex, isNil, propEq } from "ramda";
import { hasAdminRole } from "auth/authUtils";
import { setCurrentOrganization } from "auth/authActions";
import { caseInsensitiveSort } from "../util/sorts";
import { routeChanged } from "../application/redux/applicationActions";
import makeStyles from "@mui/styles/makeStyles";

import Paper from "@mui/material/Paper";
import MenuList from "@mui/material/MenuList";
import MenuItem from "@mui/material/MenuItem";
import Popper from "@mui/material/Popper";
import Grow from "@mui/material/Grow";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Typography from "@mui/material/Typography";

import SelectableMenuItem from "./SelectableMenuItem";

const useStyles = makeStyles(theme => ({
  selected: {
    backgroundColor: "transparent !important",
    "& p": {
      color: theme.palette.primary.main,
      fontWeight: 700,
    },
    "&:hover": {
      backgroundColor: `${theme.palette.primary.main} !important`,
      color: "white",
      "& p, & svg": {
        color: "white",
      },
    },
  },

  menuContainer: {
    zIndex: "1000",
    left: "-5px !important",
  },

  menu: {
    borderRadius: "5px",
    // width: "270px",
    maxWidth: 500,
    boxShadow: "0px 0px 30px rgba(149, 169, 177, 0.3)",
  },

  checkmark: {
    justifyContent: "end",
    color: theme.palette.primary.main,
  },

  avatar: {
    color: theme.palette.primary.contrastText,
    backgroundColor: theme.palette.primary.main,
  },
}));

const ORGANIZE_ROUTE = "/creator/organize?view=review,pending";

const SwitchOrgsList = ({ closeDropDown, onClose }) => {
  const auth = useSelector(state => state.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [orgs, setOrgs] = useState([]);
  const [selectedIdx, setSelectedIdx] = useState(null);

  useEffect(() => {
    const admin = {
      name: "Administrator",
      id: auth.user.id,
    };
    const sortedOrgs = caseInsensitiveSort("name")(auth.user.organizations);

    hasAdminRole(auth.user)
      ? setOrgs([admin, ...sortedOrgs])
      : setOrgs(sortedOrgs);
  }, [auth.user]);

  useEffect(() => {
    if (isNil(auth.currentOrganization)) {
      setSelectedIdx(0);
    } else {
      const currentOrg = findIndex(
        propEq("id", auth.currentOrganization.id),
        orgs,
      );
      setSelectedIdx(currentOrg);
    }
  }, [auth.currentOrganization, orgs]);

  const handleClick = (org, idx) => {
    if (equals(org.name, "Administrator")) {
      org = null;
    }
    setSelectedIdx(idx);
    dispatch(
      setCurrentOrganization({ organization: org, userId: auth.user.id }),
    );
    // dispatching route change is deprecated, used to change search calls in older manage media components
    dispatch(routeChanged(ORGANIZE_ROUTE));
    navigate(ORGANIZE_ROUTE);
    onClose();
    closeDropDown();
  };

  return (
    <Fragment>
      {auth.user.organizations &&
        orgs.map((org, idx) => (
          <SelectableMenuItem
            key={org.id}
            label={org.name}
            MenuItemProps={{
              onClick: () => handleClick(org, idx),
              selected: equals(selectedIdx, idx),
            }}
          />
        ))}
    </Fragment>
  );
};

const UserProfileSwitchOrgsMenu = props => {
  const classes = useStyles();

  const { closeDropDown, onClose } = props;

  const anchorRef = useRef(null);
  const [openMenu, setOpenMenu] = useState(false);
  const toggleMenu = () => setOpenMenu(prevOpen => !prevOpen);
  const closeMenu = event => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpenMenu(false);
  };

  const handleListKeyDown = event => {
    if (event.key === "Tab") {
      event.preventDefault();
      setOpenMenu(false);
    }
  };

  const prevOpen = useRef(openMenu);
  useEffect(() => {
    if (prevOpen.current === true && openMenu === false) {
      anchorRef.current.focus();
    }
    prevOpen.current = openMenu;
  }, [openMenu]);

  return (
    <Fragment>
      <MenuItem
        ref={anchorRef}
        onClick={toggleMenu}
        classes={{ root: classes.menuItem }}
        aria-controls={openMenu ? "switch-organization" : undefined}
        aria-haspopup="true"
        disableRipple
      >
        <Typography>Switch Organization</Typography>
      </MenuItem>
      <Popper
        className={classes.menuContainer}
        open={openMenu}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        placement={"left-start"}
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps}>
            <Paper classes={{ root: classes.menu }} elevation={0}>
              <ClickAwayListener onClickAway={closeMenu}>
                <MenuList
                  className={classes.menu}
                  autoFocusItem={openMenu}
                  id="switch-organization"
                  onKeyDown={handleListKeyDown}
                >
                  <SwitchOrgsList
                    onClose={onClose}
                    closeDropDown={closeDropDown}
                  />
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </Fragment>
  );
};

export default UserProfileSwitchOrgsMenu;
